import { blinkConstants } from '@thoughtspot/blink-constants';
import { envFlags, FLAGS } from '@thoughtspot/env-flags';
import { useTranslation } from '@thoughtspot/i18n';
import { UserAction, userActionTypeEnum } from '@thoughtspot/metrics';
import { Button } from '@thoughtspot/radiant-react/widgets/button';
import { Divider } from '@thoughtspot/radiant-react/widgets/divider';
import { IconColor, IconID } from '@thoughtspot/radiant-react/widgets/icon';
import { Horizontal } from '@thoughtspot/radiant-react/widgets/layout';
import { Menu } from '@thoughtspot/radiant-react/widgets/menu';
import { Popover } from '@thoughtspot/radiant-react/widgets/popover';
import { SearchBar } from '@thoughtspot/radiant-react/widgets/search-bar';
import cx from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styles from './org-menu.module.scss';
export const KEYBOARD_KEYS = {
    TAB: 'Tab',
    ARROW_DOWN: 'ArrowDown',
    ARROW_UP: 'ArrowUp',
    ENTER: 'Enter',
};
const SELECTORS = {
    SEARCH_INPUT: 'input[type="text"]',
    ORG_BUTTON: '[data-testid="orgBtnTextHolderId"]',
};
export const accessibleOrgParams = {
    batchsize: -1,
    offset: -1,
    pattern: '',
};
export const OrgMenu = ({ getCurrentOrgId, orgsListData, setSelectedOrgId, refetch, selectedOrgId, isLegacy = false, isOrgPerUrlEnabled = false, areProfileAndHelpHidden = false, getSubDomainIfOrgEnabled, isNavigationV3Enabled = false, metricsService, }) => {
    const { t } = useTranslation();
    const [showOrgMenu, setShowOrgMenu] = useState(false);
    const [defaultOrg, setDefaultOrg] = useState(null);
    const [currentCheckMark, setCurrentCheckMark] = useState();
    const [orgList, setOrgList] = useState([]);
    const [searchQuery, setSearchQuery] = React.useState('');
    const orgPopoverRef = useRef(null);
    const isSearchUntouched = useRef(true);
    const menuRef = useRef(null);
    const [focusedItemIndex, setFocusedItemIndex] = useState(-1);
    const searchBarRef = useRef(null);
    const buttonRef = useRef(null);
    const handleKeyDown = useCallback((e) => {
        if (!showOrgMenu)
            return;
        switch (e.key) {
            case KEYBOARD_KEYS.TAB:
            case KEYBOARD_KEYS.ARROW_DOWN:
                e.preventDefault();
                if (focusedItemIndex === -1) {
                    searchBarRef.current.focus();
                    setFocusedItemIndex(0);
                }
                else {
                    setFocusedItemIndex(prevIndex => prevIndex < orgList.length - 1 ? prevIndex + 1 : 0);
                }
                break;
            case KEYBOARD_KEYS.ARROW_UP:
                e.preventDefault();
                setFocusedItemIndex(prevIndex => prevIndex > 0 ? prevIndex - 1 : orgList.length - 1);
                break;
            case KEYBOARD_KEYS.ENTER:
                e.preventDefault();
                if (focusedItemIndex >= 0 &&
                    focusedItemIndex < orgList.length) {
                    const selectedOrg = orgList[focusedItemIndex];
                    if (String(getCurrentOrgId()) !== selectedOrg.orgId) {
                        setDefaultOrg(selectedOrg);
                        setCurrentCheckMark(selectedOrg.orgId);
                        setSelectedOrgId(selectedOrg.orgId);
                    }
                    setShowOrgMenu(false);
                    buttonRef.current?.focus();
                }
                break;
            default:
                break;
        }
    }, [
        showOrgMenu,
        orgList,
        focusedItemIndex,
        getCurrentOrgId,
        setSelectedOrgId,
    ]);
    let currentOrgId = getCurrentOrgId();
    if (isOrgPerUrlEnabled && !getSubDomainIfOrgEnabled()) {
        const overrideOrgId = envFlags.getValue(FLAGS.overrideOrgId.name);
        if (overrideOrgId >= 0) {
            currentOrgId = overrideOrgId;
        }
    }
    if (currentOrgId === blinkConstants.orgs.ALL_ID) {
        currentOrgId = blinkConstants.orgs.MASTER_ID;
    }
    const toggle = () => {
        setShowOrgMenu(prevState => !prevState);
        metricsService.collectEvent(new UserAction(userActionTypeEnum.USER_PROFILE_ORG_SWITCHER_CLICK), {});
    };
    const updateSearchQuery = useCallback((query) => {
        isSearchUntouched.current = false;
        setSearchQuery(query);
    }, [searchQuery]);
    useEffect(() => {
        if (menuRef.current) {
            const searchBarElement = menuRef.current.querySelector(SELECTORS.SEARCH_INPUT);
            if (searchBarElement) {
                searchBarRef.current = searchBarElement;
            }
        }
    }, [showOrgMenu]);
    useEffect(() => {
        if (showOrgMenu) {
            if (searchBarRef.current) {
                searchBarRef.current.focus();
            }
            else if (menuRef.current) {
                menuRef.current.focus();
            }
        }
        else {
            document?.querySelector(SELECTORS.ORG_BUTTON)?.blur();
        }
    }, [showOrgMenu]);
    useEffect(() => {
        if (focusedItemIndex >= 0 && focusedItemIndex < orgList.length) {
            const focusedElement = document.getElementById(`org-menu-item-${focusedItemIndex}`);
            if (focusedElement) {
                focusedElement.focus();
            }
        }
    }, [focusedItemIndex, orgList]);
    const selectOrgList = (orgs) => {
        const currentOrgData = orgs.filter(org => org.orgId === String(currentOrgId));
        const orgId = currentOrgData[0]?.orgId || currentOrgId;
        setDefaultOrg(currentOrgData[0]);
        setCurrentCheckMark(orgId);
    };
    const onMenuButtonClick = useCallback(() => {
        refetch();
        toggle();
    }, [refetch, toggle]);
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (orgPopoverRef.current &&
                showOrgMenu &&
                !orgPopoverRef.current.contains(event.target)) {
                updateSearchQuery('');
                setShowOrgMenu(false);
            }
        };
        const handleMessage = (event) => {
            if (event.data?.messageType === 'iframeClickForOrgMenu') {
                handleClickOutside(event);
            }
        };
        window.addEventListener('message', handleMessage);
        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, [showOrgMenu]);
    useEffect(() => {
        if (orgsListData && !selectedOrgId) {
            const list = [...orgsListData];
            setOrgList(list);
            if (isSearchUntouched.current) {
                selectOrgList(list);
            }
        }
    }, [orgsListData]);
    useEffect(() => {
        refetch({
            inputData: {
                ...accessibleOrgParams,
                pattern: searchQuery,
            },
        });
    }, [searchQuery]);
    const selectContent = () => {
        return (React.createElement("div", { ref: menuRef, tabIndex: 0, role: "menu", onKeyDown: handleKeyDown, "aria-label": "Organization Menu" },
            React.createElement(Menu, { id: "Menu", show: showOrgMenu, className: styles.orgMenuContainer },
                React.createElement("div", { className: styles.orgMenuGroupList },
                    React.createElement(React.Fragment, null,
                        React.createElement(SearchBar, { searchValue: searchQuery, className: styles.filterInput, searchPlaceholder: t('SEARCH_PLACEHOLDER'), onSearchChange: updateSearchQuery, onGrayBackground: true, suffixIcon: searchQuery.length && IconID.CROSS, onSuffixIconClick: () => updateSearchQuery('') }),
                        React.createElement(Divider, null)),
                    React.createElement("div", { className: styles.orgMenuGroupListItems },
                        orgList.map((org, index) => (React.createElement(Horizontal, { key: org.orgId, className: cx(styles.orgMenuCommonItem, {
                                [styles.orgMenuItem]: org.orgId === currentCheckMark,
                                [styles.orgMenuItemWithoutIcon]: org.orgId !== currentCheckMark,
                                [styles.primaryOrgMenuItem]: org.orgId ===
                                    String(blinkConstants.orgs.MASTER_ID),
                                [styles.focusedMenuItem]: index === focusedItemIndex,
                            }), id: `org-menu-item-${index}`, tabIndex: 0 },
                            React.createElement(Menu.Item, { label: org.orgName, id: org.orgId, iconName: IconID.CHECKMARK, onSelect: () => {
                                    if (String(getCurrentOrgId()) !==
                                        org.orgId) {
                                        setDefaultOrg(org);
                                        setCurrentCheckMark(org.orgId);
                                        setSelectedOrgId(org.orgId);
                                    }
                                    setShowOrgMenu(false);
                                    buttonRef.current?.focus();
                                }, rightText: org.orgId ===
                                    String(blinkConstants.orgs.MASTER_ID)
                                    ? t('ORGS_PRIMARY_LABEL')
                                    : '' })))),
                        searchQuery && !orgList.length && (React.createElement(Menu.Item, { label: t('NO_MATCH_MSG'), id: "noMatchesFound", iconName: IconID.NO_RESULT_FOUND })))))));
    };
    return (React.createElement(Horizontal, { "data-testid": "org-content", className: cx({
            [styles.orgMenuContainerV3]: isNavigationV3Enabled,
            [styles.orgMenuContainerV3Expanded]: isNavigationV3Enabled && showOrgMenu,
        }) }, isSearchUntouched.current && orgList.length === 1 ? (React.createElement("div", { className: cx({ [styles.orgButton]: isLegacy }) },
        React.createElement("div", { ref: buttonRef },
            React.createElement(Button, { className: cx(styles.orgButtonTextWrap, {
                    [styles.globalOrgButtonTextWrap]: !isLegacy,
                    [styles.globalOrgButtonTextWrapWithNoHover]: !isLegacy,
                    [styles.globalNavButtonTextWrap]: !areProfileAndHelpHidden,
                }), iconColor: isLegacy
                    ? IconColor.WHITE
                    : showOrgMenu && IconColor.BLACK, iconPlacement: "right", type: isLegacy ? 'tertiary' : 'secondary', text: defaultOrg && defaultOrg.orgName, size: "m", onClick: onMenuButtonClick, "data-testid": "orgBtnTextHolderId" })))) : (React.createElement(React.Fragment, null,
        React.createElement("div", { ref: orgPopoverRef, className: cx({ [styles.orgButton]: isLegacy }) },
            React.createElement("div", { ref: buttonRef },
                React.createElement(Button, { className: cx(styles.orgButtonTextWrap, {
                        [styles.globalOrgButtonTextWrap]: !isLegacy,
                        [styles.globalNavButtonTextWrap]: !areProfileAndHelpHidden,
                        [styles.toggleOrgButtonCls]: !isLegacy && showOrgMenu,
                        [styles.orgButtonV3]: isNavigationV3Enabled,
                    }), icon: IconID.CHEVRON_DOWN, iconColor: isLegacy || isNavigationV3Enabled
                        ? IconColor.WHITE
                        : showOrgMenu && IconColor.BLACK, iconPlacement: "right", type: isLegacy && !isNavigationV3Enabled
                        ? 'tertiary'
                        : 'secondary', text: defaultOrg && defaultOrg.orgName, size: "m", onClick: onMenuButtonClick, "data-testid": "orgBtnTextHolderId" }))),
        React.createElement(Popover, { className: styles.orgPopover, bottomOffset: 8, targetRef: orgPopoverRef, content: selectContent(), isVisible: showOrgMenu, align: "bottom-start", appendToBody: true, onClickOutside: () => {
                updateSearchQuery('');
                setShowOrgMenu(false);
            }, onEscape: (e) => {
                updateSearchQuery('');
                setFocusedItemIndex(-1);
                setShowOrgMenu(false);
            } })))));
};
//# sourceMappingURL=org-menu.js.map